// Debounce mechanism for strikethrough updates
let updateStrikethroughTimeout = null;
let isUpdatingStrikethrough = false;
let cachedCompareAtPrices = {};

// Global function to update strikethrough pricing consistently
window.updateStrikethroughPricing = async function(immediate = false) {
  // Clear any pending updates
  if (updateStrikethroughTimeout) {
    clearTimeout(updateStrikethroughTimeout);
    updateStrikethroughTimeout = null;
  }
  
  // If already updating, queue another update unless immediate is requested
  if (isUpdatingStrikethrough && !immediate) {
    updateStrikethroughTimeout = setTimeout(() => {
      window.updateStrikethroughPricing(true);
    }, 200);
    return;
  }
  
  // Debounce unless immediate update is requested
  if (!immediate) {
    updateStrikethroughTimeout = setTimeout(() => {
      window.updateStrikethroughPricing(true);
    }, 200);
    return;
  }
  
  try {
    isUpdatingStrikethrough = true;
    
    // Fetch cart data
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    const totalPricesContainer = document.querySelector('.cart__total-prices');
    if (!totalPricesContainer || !cart || !cart.items || cart.items.length === 0) {
      // Remove strikethrough if cart is empty
      const existingStrikethrough = totalPricesContainer?.querySelector('.cart__original-total');
      if (existingStrikethrough) {
        existingStrikethrough.remove();
      }
      return;
    }
    
    // Get unique product handles to fetch product data
    const productHandles = [...new Set(cart.items.map(item => item.handle))];
    
    // Only fetch product data for handles we don't have cached
    const handlesToFetch = productHandles.filter(handle => !cachedCompareAtPrices[handle]);
    
    // Fetch product data for uncached products
    if (handlesToFetch.length > 0) {
      const productPromises = handlesToFetch.map(handle => 
        fetch(`/products/${handle}.js`)
          .then(res => res.json())
          .catch(err => {
            return null;
          })
      );
      
      const products = await Promise.all(productPromises);
      
      // Update cache with new product data
      products.forEach(product => {
        if (product && product.variants) {
          cachedCompareAtPrices[product.handle] = {};
          product.variants.forEach(variant => {
            if (variant.compare_at_price) {
              cachedCompareAtPrices[product.handle][variant.id] = variant.compare_at_price;
            }
          });
        }
      });
    }
    
    // Build variant compare-at prices from cache
    const variantCompareAtPrices = {};
    productHandles.forEach(handle => {
      if (cachedCompareAtPrices[handle]) {
        Object.assign(variantCompareAtPrices, cachedCompareAtPrices[handle]);
      }
    });
    
    let originalSubtotal = 0;
    
    // Calculate original subtotal using ONLY fetched compare_at_prices for consistency
    cart.items.forEach((item) => {
      const compareAtPrice = variantCompareAtPrices[item.variant_id];
      
      // Log for debugging
      if (window.location.search.includes('debug')) {
        console.log('Item pricing:', {
          handle: item.handle,
          variant_id: item.variant_id,
          fetchedCompareAt: compareAtPrice,
          cartCompareAt: item.compare_at_price,
          price: item.price,
          original_price: item.original_price,
          quantity: item.quantity
        });
      }
      
      // Use compare_at_price when available from fetched data and greater than current price
      if (compareAtPrice && compareAtPrice > item.price) {
        const itemTotal = compareAtPrice * item.quantity;
        originalSubtotal += itemTotal;
      } else {
        // Use original_price as fallback (not compare_at from cart API which may be wrong)
        const itemTotal = item.original_price * item.quantity;
        originalSubtotal += itemTotal;
      }
    });
    
    // Determine if we should show strikethrough
    let shouldShowStrikethrough = false;
    
    // Show if there's a price difference
    if (originalSubtotal > cart.total_price) {
      shouldShowStrikethrough = true;
    }
    
    // Show if there are discounts
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      shouldShowStrikethrough = true;
    }
    
    if (cart.total_discount && cart.total_discount > 0) {
      shouldShowStrikethrough = true;
    }
    
    if (shouldShowStrikethrough) {
      const formattedOriginal = window.Shopify.formatMoney(originalSubtotal, window.theme.money_format);
      
      let strikethroughSpan = totalPricesContainer.querySelector('.cart__original-total');
      const cartTotal = totalPricesContainer.querySelector('cart-total');
      
      if (!strikethroughSpan) {
        strikethroughSpan = document.createElement('span');
        strikethroughSpan.className = 'cart__original-total';
        strikethroughSpan.style.cssText = 'text-decoration: line-through; color: #999; margin-right: 8px;';
        
        if (cartTotal) {
          totalPricesContainer.insertBefore(strikethroughSpan, cartTotal);
          cartTotal.style.color = '#28a745';
          cartTotal.style.fontWeight = '600';
        }
      }
      
      strikethroughSpan.textContent = formattedOriginal;
      
      // Log final value for debugging
      if (window.location.search.includes('debug')) {
        console.log('Strikethrough updated to:', formattedOriginal, 'raw:', originalSubtotal);
      }
    } else {
      // Remove strikethrough if not needed
      const existingStrikethrough = totalPricesContainer.querySelector('.cart__original-total');
      if (existingStrikethrough) {
        existingStrikethrough.remove();
        const cartTotal = totalPricesContainer.querySelector('cart-total');
        if (cartTotal) {
          cartTotal.style.color = '';
          cartTotal.style.fontWeight = '';
        }
      }
    }
  } catch (error) {
    // Silently handle errors
  } finally {
    isUpdatingStrikethrough = false;
  }
};

class InteractiveCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const debounce = (f, delay) => {
      let timer = 0;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => f.apply(this, args), delay);
      };
    };

    this.id = this.getAttribute('id');
    this.form = this.querySelector('form');
    this.cart = window.theme.cart.store.getState().products;
    this.freeShippingBar = this.querySelector('free-shipping-bar');

    this._cartDraw = this.closest('.cart-draw');
    this._controlHeightAtValue = null;
    this._resizeObserver = null;
    if (this.getAttribute('height-control') && this._cartDraw) {
      this._controlHeightAtValue = Number(this.getAttribute('height-control'));
      this._resizeObserver = new ResizeObserver(
        debounce(this._onResize.bind(this), 100),
      );

      this._resizeObserver.observe(this._cartDraw);
    }

    this._render();
    
    // Update strikethrough pricing on initial load (debounced)
    window.updateStrikethroughPricing();
    
    // Check for discount code in URL parameters after cart is initialized
    if (this.cart && this.cart.item_count > 0) {
      setTimeout(() => this._applyDiscountFromURL(), 100);
    }

    this.unsubscribe = window.theme.cart.store.subscribe((state, prevState) => {
      this.cart = state.products;
      
      // Check if cart went from empty to having items
      const wasEmpty = prevState && prevState.products.item_count === 0;
      const nowHasItems = state.products.item_count > 0;
      
      this._render();

      this._updateCartDiscounts();
      this._updateCartTotal();
      this._updateFreeShipping();
      
      // Update strikethrough pricing after cart changes
      window.updateStrikethroughPricing();
      
      // Apply discount from URL when cart gets its first item
      if (wasEmpty && nowHasItems) {
        this._applyDiscountFromURL();
      }
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  _onResize(entries) {
    entries.forEach(() => {
      const parentElement = this.parentElement;
      const parentElementComputedStyle = window.getComputedStyle(parentElement);
      const gap = Number(
        parentElementComputedStyle
          .getPropertyValue('row-gap')
          .replace('px', ''),
      );
      const parentElementTopPadding = Number(
        parentElementComputedStyle
          .getPropertyValue('padding-top')
          .replace('px', ''),
      );
      const siblingElements = Array.from(
        this.parentElement.querySelectorAll(
          '.cart-draw__announcement, .cart-draw__head, .free-shipping-bar',
        ),
      );
      const cartControls = this.querySelector('.cart__controls');

      if (!cartControls) return;

      const blockedHeight =
        parentElementTopPadding +
        siblingElements.reduce(
          (total, element) => total + element.offsetHeight + gap,
          0,
        ) +
        cartControls.offsetHeight;

      this._cartDraw.classList.toggle(
        'is-scrollable',
        this._controlHeightAtValue > window.innerHeight - blockedHeight,
      );
    });
  }

  _render() {
    if (this.cart.item_count > 0) {
      this._renderCart();
    } else {
      this._renderEmptyCart();
    }
  }

  _renderCart() {
    if (this.querySelector('cart-full')) return;

    const cartTemplate = document.getElementById('template-cart').content;
    const cartFragment = cartTemplate.cloneNode(true);
    const cartNote = cartFragment.querySelector('cart-note');

    const emptyCart = this.querySelector('cart-empty');

    if (emptyCart) {
      emptyCart.remove();
    }

    this.appendChild(cartFragment);

    if (cartNote) {
      // cartNote.value = this.cart.note;
      cartNote.querySelector('cart-textarea').value = this.cart.note;
    }
    this._updateCartDiscounts();
    this._updateCartTotal();
    
    // Always use the global debounced function to update strikethrough pricing
    if (typeof updateStrikethroughPricing === 'function') {
      updateStrikethroughPricing();
    }
  }

  _renderEmptyCart() {
    if (this.querySelector('cart-empty')) return;

    const cartEmptyTemplate = document.getElementById(
      'template-empty-cart',
    ).content;
    const cartEmptyFragment = cartEmptyTemplate.cloneNode(true);

    const cartFull = this.querySelector('cart-full');

    if (cartFull) {
      cartFull.remove();
    }

    this.appendChild(cartEmptyFragment);
  }

  _updateFreeShipping() {
    if (!this.freeShippingBar) return;

    console.log('[InteractiveCart] _updateFreeShipping - cart.total_price:', this.cart.total_price, 'cart:', this.cart);
    this.freeShippingBar.setAttribute('value', this.cart.total_price);

    // Also try to directly trigger update if the method exists
    if (this.freeShippingBar._update && typeof this.freeShippingBar._update === 'function') {
      this.freeShippingBar.value = this.cart.total_price;
      this.freeShippingBar._update();
    }
  }

  async _applyDiscountFromURL() {
    // Check for discount_code in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const discountCode = urlParams.get('discount_code') || urlParams.get('discount');
    
    if (!discountCode) {
      console.log('No discount code in URL');
      return;
    }
    
    console.log('Found discount code in URL:', discountCode);
    
    // Don't wait if cart already has items
    if (!this.cart || this.cart.item_count === 0) {
      console.log('Cart is empty, waiting for items...');
      return; // Will be called again when items are added
    }
    
    try {
      
      // Check if discount is already applied
      if (this.cart.cart_level_discount_applications) {
        const isAlreadyApplied = this.cart.cart_level_discount_applications.some(
          discount => discount.title.toLowerCase() === discountCode.toLowerCase()
        );
        
        if (isAlreadyApplied) {
          console.log('Discount code already applied:', discountCode);
          return;
        }
      }
      
      console.log('Applying discount code from URL:', discountCode);
      
      // Try iframe method first as it's more reliable
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = '/discount/' + encodeURIComponent(discountCode);
      document.body.appendChild(iframe);
      
      // Wait for discount to be applied
      await new Promise(resolve => setTimeout(resolve, 1500));
      document.body.removeChild(iframe);
      
      // Fetch updated cart
      const verifyResponse = await fetch('/cart.js');
      const verifyCart = await verifyResponse.json();
      
      // Update cart state
      this.cart = verifyCart;
      
      // Check if discount was applied
      let discountApplied = false;
      if (verifyCart.cart_level_discount_applications && 
          verifyCart.cart_level_discount_applications.length > 0) {
        discountApplied = verifyCart.cart_level_discount_applications.some(
          discount => discount.title.toLowerCase() === discountCode.toLowerCase()
        );
      }
      
      if (discountApplied) {
        console.log('Discount code applied successfully:', discountCode);
        
        // Update UI
        this._updateCartDiscounts();
        this._updateCartTotal();
        
        // Trigger strikethrough update once with debouncing
        if (typeof updateStrikethroughPricing === 'function') {
          updateStrikethroughPricing();
        }
        
        // Remove discount_code from URL
        const url = new URL(window.location);
        url.searchParams.delete('discount_code');
        url.searchParams.delete('discount');
        window.history.replaceState({}, '', url);
      } else {
        console.log('Discount code was not applied, may be invalid:', discountCode);
      }
    } catch (error) {
      console.error('Error applying discount code from URL:', error);
    }
  }

  _updateCartTotal() {
    // Find ALL cart-total elements (could be multiple)
    const cartTotals = this.querySelectorAll('cart-total');
    if (!cartTotals || cartTotals.length === 0) return;

    cartTotals.forEach(cartTotal => {
      // Before updating, check if we have a strikethrough price
      const parentContainer = cartTotal.closest('.cart__total-prices');
      let hasStrikethrough = false;
      let strikethroughElement = null;
      
      if (parentContainer) {
        strikethroughElement = parentContainer.querySelector('.cart__original-total');
        hasStrikethrough = !!strikethroughElement;
      }
      
      // Update ONLY the cart-total innerHTML, nothing else
      const newTotal = window.Shopify.formatMoney(
        this.cart.total_price,
        window.theme.money_total_price_format,
      );
      
      // Check if cart-total is empty (which happens on page load)
      if (!cartTotal.textContent || cartTotal.textContent.trim() === '') {
        // Set the initial total
        cartTotal.innerHTML = newTotal;
        
        // If this is the final total and has style attribute, preserve it
        if (cartTotal.classList.contains('cart__final-total')) {
          const styleAttr = cartTotal.getAttribute('style');
          if (styleAttr) {
            cartTotal.setAttribute('style', styleAttr);
          }
        }
      } else {
        // Only update if the value actually changed
        if (cartTotal.innerHTML !== newTotal) {
          cartTotal.innerHTML = newTotal;
        }
      }
      
      // If we had a strikethrough and it's gone, restore it
      if (hasStrikethrough && parentContainer && !parentContainer.querySelector('.cart__original-total')) {
        parentContainer.insertBefore(strikethroughElement, cartTotal);
      }
    });
    
    // Maintain strikethrough display
    if (typeof maintainStrikethroughDisplay === 'function') {
      maintainStrikethroughDisplay();
    }
    
    // Trigger strikethrough pricing update to ensure it's displayed
    if (typeof updateStrikethroughPricing === 'function') {
      updateStrikethroughPricing();
    }
  }

  _updateCartDiscounts() {
    const discountsContainer = this.querySelector('cart-discounts');
    if (!discountsContainer) return;

    const discounts = discountsContainer.querySelector('discount-list');
    // Liquid-rendered cart discount data differs from the one gotten via AJAX
    const discountApplications = this.cart.cart_level_discount_applications;
    
    // Calculate item-level discounts (compare_at_price - price)
    let totalItemDiscounts = 0;
    if (this.cart && this.cart.items) {
      // Fetch compare_at_prices for accurate calculation
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          let itemDiscountTotal = 0;
          const productHandles = [...new Set(cart.items.map(item => item.handle))];
          
          // Fetch product data to get compare_at_prices
          Promise.all(productHandles.map(handle => 
            fetch(`/products/${handle}.js`)
              .then(res => res.json())
              .catch(() => null)
          )).then(products => {
            const variantCompareAtPrices = {};
            products.forEach(product => {
              if (product && product.variants) {
                product.variants.forEach(variant => {
                  if (variant.compare_at_price) {
                    variantCompareAtPrices[variant.id] = variant.compare_at_price;
                  }
                });
              }
            });
            
            // Calculate item-level discounts
            cart.items.forEach(item => {
              const compareAtPrice = variantCompareAtPrices[item.variant_id];
              if (compareAtPrice && compareAtPrice > item.price) {
                const itemDiscount = (compareAtPrice - item.price) * item.quantity;
                itemDiscountTotal += itemDiscount;
              }
              // Include line-level discount allocations (e.g., BOGO automatic discounts)
              if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
                item.line_level_discount_allocations.forEach(alloc => {
                  itemDiscountTotal += alloc.amount;
                });
              }
            });

            // Update the discount display with item-level discounts
            this._updateDiscountDisplay(discountApplications, itemDiscountTotal);
          });
        });
    } else {
      // If no cart items, just update with regular discounts
      this._updateDiscountDisplay(discountApplications, 0);
    }
  }
  
  _updateDiscountDisplay(discountApplications, itemDiscountTotal) {
    // Also update the applied discount codes display
    const appliedCodesSection = document.querySelector('.cart__applied-codes');
    if (!appliedCodesSection) return;
    
    const hasDiscounts = (discountApplications && discountApplications.length > 0) || itemDiscountTotal > 0;
    
    if (hasDiscounts) {
      // Show the applied codes section and populate it
      appliedCodesSection.style.display = 'block';
      const codesList = appliedCodesSection.querySelector('.cart__applied-codes-list');
      if (codesList) {
        // Build new content in a document fragment first
        const fragment = document.createDocumentFragment();
        
        // Add item-level discount if exists
        if (itemDiscountTotal > 0) {
          const itemDiscountDiv = document.createElement('div');
          itemDiscountDiv.className = 'cart__applied-code-item';
          itemDiscountDiv.dataset.code = 'item-discounts';
          itemDiscountDiv.innerHTML = `
            <span class="cart__applied-code-icon">✓</span>
            <span class="cart__applied-code-text">Sale Savings</span>
            <span class="cart__applied-code-amount">-${window.Shopify.formatMoney(itemDiscountTotal, window.theme.money_format)}</span>
          `;
          fragment.appendChild(itemDiscountDiv);
        }
        
        // Add regular discount codes
        if (discountApplications) {
          discountApplications.forEach(discount => {
            // Skip if discount title is undefined or null
            if (!discount.title) return;
            
            const codeItem = document.createElement('div');
            codeItem.className = 'cart__applied-code-item';
            codeItem.dataset.code = discount.title;
            codeItem.innerHTML = `
              <span class="cart__applied-code-icon">✓</span>
              <span class="cart__applied-code-text">${discount.title}</span>
              <span class="cart__applied-code-amount">-${window.Shopify.formatMoney(discount.total_allocated_amount, window.theme.money_format)}</span>
            `;
            fragment.appendChild(codeItem);
          });
        }

        // Update DOM with all discounts
        codesList.innerHTML = '';
        codesList.appendChild(fragment);
      }
    } else {
      appliedCodesSection.style.display = 'none';
    }
    
    // Always update strikethrough when discounts change (debounced)
    if (typeof updateStrikethroughPricing === 'function') {
      updateStrikethroughPricing();
    }
    const discountItems =
      discountApplications.length > 0 &&
      discountApplications[0].hasOwnProperty('discount_application')
        ? discountApplications.reduce(
            (
              items,
              {
                discount_application: {
                  key,
                  title,
                  total_allocated_amount: amount,
                },
              },
            ) => {
              const sameKeyItemIndex = items.findIndex((i) => i.key === key);

              if (sameKeyItemIndex > -1) {
                items[sameKeyItemIndex].amount += amount;
                return items;
              }

              items.push({
                key,
                title,
                amount,
              });

              return items;
            },
            [],
          )
        : discountApplications.reduce(
            (items, { key, title, total_allocated_amount: amount }) => {
              items.push({
                key,
                title,
                amount,
              });

              return items;
            },
            [],
          );

    this.classList.toggle('has-cart-discounts', discountItems.length);

    const discountItemsString = JSON.stringify(discountItems);
    discounts.setAttribute('items', discountItemsString);
  }
}
customElements.define('interactive-cart', InteractiveCart);

class CartItems extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('cart-item'));
    this.giftWrappingProductId = this.getAttribute('gift-wrapping-product-id');

    this.unsubscribe = window.theme.cart.store.subscribe((state) => {
      // Update strikethrough pricing on any cart change (debounced)
      if (typeof updateStrikethroughPricing === 'function') {
        updateStrikethroughPricing();
      }
      this.items = this.items.reduce((newItems, item) => {
        const key = item.getAttribute('key');
        const itemInCart = state.products.items.find((i) => i.key === key);

        if (!itemInCart) {
          item.remove();
          return newItems;
        }

        newItems.push(item);
        return newItems;
      }, []);

      const currentItemsKeys = this.items.map((item) => item.key);
      const newItems = [];
      let hasNewItems = false;
      
      state.products.items.forEach((item, i) => {
        if (!currentItemsKeys.includes(item.key)) {
          hasNewItems = true;
          const newCartItem = this._createCartItem(item);
          if (i === 0) {
            this.prepend(newCartItem);
          } else {
            newItems[i - 1].after(newCartItem);
          }
          newItems.push(newCartItem);
        } else {
          const oldItem = this.items.find((i) => i.key === item.key);
          if (currentItemsKeys.indexOf(oldItem.key) !== i) {
            if (i === 0) {
              this.prepend(oldItem);
            } else {
              newItems[i - 1].after(oldItem);
            }
          }
          newItems.push(oldItem);
        }
      });
      this.items = newItems;
      
      // If new items were added, refresh all cart HTML to get compare-at prices
      if (hasNewItems) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          this._refreshAllCartItemsHTML();
        }, 200);
        
        // Also do a quick cart.js fetch to trigger subtotal update
        // Add multiple delays to ensure the strikethrough gets updated properly
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            // Update strikethrough with immediate flag to ensure it updates
            if (typeof updateStrikethroughPricing === 'function') {
              updateStrikethroughPricing(true); // Use immediate flag to bypass debounce
            }
          });
      }
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  _createCartItem(item) {
    const cartItemTemplate =
      document.getElementById('template-cart-item').content;
    const cartItemFragment = cartItemTemplate.cloneNode(true);
    const cartItem = cartItemFragment.querySelector('cart-item');
    cartItem.setAttribute('key', item.key);
    
    // The refresh will be handled by _refreshAllCartItemsHTML after all new items are added
    
    return cartItem;
  }
  
  _refreshItemPriceDisplay(cartItem, item) {
    // Quick price update for individual items
    // This runs immediately without waiting for network requests
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartItemData = cart.items.find(i => i.key === item.key);
        if (!cartItemData) return;
        
        // Try multiple times to find the price elements (they might be rendering)
        const tryUpdatePrices = (attempts = 0) => {
          const originalPriceEl = cartItem.querySelector('cart-item-original-line-price');
          const finalPriceEl = cartItem.querySelector('cart-item-final-line-price');
          
          if (!originalPriceEl || !finalPriceEl) {
            if (attempts < 10) {
              setTimeout(() => tryUpdatePrices(attempts + 1), 100);
            }
            return;
          }
          
          // Clear any existing content first
          originalPriceEl.innerHTML = '';
          finalPriceEl.innerHTML = '';
          
          // Check if item has compare_at_price AND it's greater than the actual price
          if (cartItemData.compare_at_price && cartItemData.compare_at_price > cartItemData.price) {
            const compareLinePrice = cartItemData.compare_at_price * cartItemData.quantity;
            // Use final_line_price to include all discounts (including cart-level discount codes)
            const actualLinePrice = cartItemData.final_line_price;
            
            // Update original price with strikethrough
            originalPriceEl.innerHTML = `
              <div class="price price--original">
                <span class="money"><money-amount>${window.Shopify.formatMoney(compareLinePrice, window.theme.money_format)}</money-amount></span>
              </div>
            `;
            originalPriceEl.dataset.hasCompareAt = 'true';
            originalPriceEl.dataset.compareAtPrice = cartItemData.compare_at_price;
            
            // Update final price with sale styling
            finalPriceEl.innerHTML = `
              <div class="price price--sale">
                <span class="money"><money-amount>${window.Shopify.formatMoney(actualLinePrice, window.theme.money_format)}</money-amount></span>
              </div>
            `;
            finalPriceEl.dataset.hasCompareAt = 'true';
          } else if (cartItemData.original_line_price !== cartItemData.final_line_price) {
            // No compare-at price, but there's a discount (cart-level or line-level)
            originalPriceEl.innerHTML = `
              <div class="price price--original">
                <span class="money"><money-amount>${window.Shopify.formatMoney(cartItemData.original_line_price, window.theme.money_format)}</money-amount></span>
              </div>
            `;
            
            finalPriceEl.innerHTML = `
              <div class="price price--sale">
                <span class="money"><money-amount>${window.Shopify.formatMoney(cartItemData.final_line_price, window.theme.money_format)}</money-amount></span>
              </div>
            `;
          } else {
            // No compare-at price and no discount, show regular price
            const linePrice = cartItemData.original_line_price;
            finalPriceEl.innerHTML = `
              <div class="price">
                <span class="money"><money-amount>${window.Shopify.formatMoney(linePrice, window.theme.money_format)}</money-amount></span>
              </div>
            `;
          }
          
          // Also trigger subtotal update
          if (typeof updateStrikethroughPricing === 'function') {
            updateStrikethroughPricing();
          }
        };
        
        tryUpdatePrices();
      })
      .catch(error => {
        console.error('Error refreshing item price:', error);
      });
  }
  
  _refreshAllCartItemsHTML() {
    // Fetch fresh cart HTML with proper compare-at prices from server
    // Use sections API to get the main-cart section which has the cart items
    fetch('/cart?section_id=main-cart')
      .then(response => response.text())
      .then(html => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // For each cart item in DOM, replace its price sections with server-rendered HTML
        this.items.forEach(cartItem => {
          const key = cartItem.getAttribute('key');
          if (!key) return;
          
          // Find the corresponding item in the fresh HTML
          const freshItem = tempDiv.querySelector(`cart-item[key="${key}"]`);
          if (!freshItem) return;
          
          // Get the price elements from both
          const currentOriginalPrice = cartItem.querySelector('cart-item-original-line-price');
          const currentFinalPrice = cartItem.querySelector('cart-item-final-line-price');
          const freshOriginalPrice = freshItem.querySelector('cart-item-original-line-price');
          const freshFinalPrice = freshItem.querySelector('cart-item-final-line-price');
          
          // Check if we should actually show the strikethrough
          const compareAtPrice = parseInt(freshOriginalPrice?.dataset?.compareAtPrice || '0');
          const hasCompareAt = freshOriginalPrice?.dataset?.hasCompareAt === 'true';
          
          // Get the actual item data to check prices
          const cartItemData = window.theme.cart.store.getState().products.items.find(i => i.key === key);
          
          // Only update if there's actually a discount (compare_at_price > price)
          if (hasCompareAt && compareAtPrice > 0 && cartItemData) {
            // Check if compare-at price is actually greater than the current price
            if (compareAtPrice > cartItemData.price) {
              // Replace the content with fresh server-rendered HTML
              if (currentOriginalPrice && freshOriginalPrice) {
                currentOriginalPrice.innerHTML = freshOriginalPrice.innerHTML;
                // Copy data attributes
                Array.from(freshOriginalPrice.attributes).forEach(attr => {
                  if (attr.name.startsWith('data-')) {
                    currentOriginalPrice.setAttribute(attr.name, attr.value);
                  }
                });
              }
              
              if (currentFinalPrice && freshFinalPrice) {
                currentFinalPrice.innerHTML = freshFinalPrice.innerHTML;
                // Copy data attributes
                Array.from(freshFinalPrice.attributes).forEach(attr => {
                  if (attr.name.startsWith('data-')) {
                    currentFinalPrice.setAttribute(attr.name, attr.value);
                  }
                });
              }
            } else {
              // Prices are equal, clear the strikethrough
              if (currentOriginalPrice) {
                currentOriginalPrice.innerHTML = '';
                currentOriginalPrice.dataset.hasCompareAt = 'false';
              }
              
              if (currentFinalPrice) {
                // Show just the regular price without sale styling
                const linePrice = cartItemData.final_line_price;
                currentFinalPrice.innerHTML = `
                  <div class="price">
                    <span class="money"><money-amount>${window.Shopify.formatMoney(linePrice, window.theme.money_format)}</money-amount></span>
                  </div>
                `;
                currentFinalPrice.dataset.hasCompareAt = 'false';
              }
            }
          } else if (cartItemData && cartItemData.original_line_price !== cartItemData.final_line_price) {
            // No compare-at but there's a discount, show strikethrough
            if (currentOriginalPrice && freshOriginalPrice) {
              currentOriginalPrice.innerHTML = freshOriginalPrice.innerHTML;
              Array.from(freshOriginalPrice.attributes).forEach(attr => {
                if (attr.name.startsWith('data-')) {
                  currentOriginalPrice.setAttribute(attr.name, attr.value);
                }
              });
            }
            
            if (currentFinalPrice && freshFinalPrice) {
              currentFinalPrice.innerHTML = freshFinalPrice.innerHTML;
              Array.from(freshFinalPrice.attributes).forEach(attr => {
                if (attr.name.startsWith('data-')) {
                  currentFinalPrice.setAttribute(attr.name, attr.value);
                }
              });
            }
          } else {
            // No discounts at all, just copy the fresh HTML
            if (currentOriginalPrice && freshOriginalPrice) {
              currentOriginalPrice.innerHTML = freshOriginalPrice.innerHTML;
              Array.from(freshOriginalPrice.attributes).forEach(attr => {
                if (attr.name.startsWith('data-')) {
                  currentOriginalPrice.setAttribute(attr.name, attr.value);
                }
              });
            }
            
            if (currentFinalPrice && freshFinalPrice) {
              currentFinalPrice.innerHTML = freshFinalPrice.innerHTML;
              Array.from(freshFinalPrice.attributes).forEach(attr => {
                if (attr.name.startsWith('data-')) {
                  currentFinalPrice.setAttribute(attr.name, attr.value);
                }
              });
            }
          }
        });
        
        // Also trigger subtotal update
        if (typeof updateStrikethroughPricing === 'function') {
          updateStrikethroughPricing();
        }
      })
      .catch(error => {
        console.error('Error refreshing cart items HTML:', error);
      });
  }
  
  _refreshAllCartPrices() {
    // Fetch fresh cart HTML to get compare-at price data for all items
    fetch('/cart?sections=cart-drawer,main-cart')
      .then(response => response.json())
      .then(sections => {
        const tempDiv = document.createElement('div');
        const sectionHtml = sections['cart-drawer'] || sections['main-cart'];
        if (sectionHtml) {
          tempDiv.innerHTML = sectionHtml;
          
          const freshItems = tempDiv.querySelectorAll('cart-item');
          freshItems.forEach(freshItem => {
            const key = freshItem.getAttribute('key');
            if (!key) return;
            
            const currentItem = document.querySelector(`cart-item[key="${key}"]`);
            if (!currentItem) return;
            
            const freshOriginalPrice = freshItem.querySelector('cart-item-original-line-price');
            const freshFinalPrice = freshItem.querySelector('cart-item-final-line-price');
            
            if (freshOriginalPrice && freshOriginalPrice.dataset.hasCompareAt === 'true') {
              const currentOriginalPrice = currentItem.querySelector('cart-item-original-line-price');
              const currentFinalPrice = currentItem.querySelector('cart-item-final-line-price');
              
              if (currentOriginalPrice && freshOriginalPrice) {
                currentOriginalPrice.innerHTML = freshOriginalPrice.innerHTML;
                currentOriginalPrice.dataset.hasCompareAt = 'true';
                currentOriginalPrice.dataset.compareAtPrice = freshOriginalPrice.dataset.compareAtPrice;
              }
              
              if (currentFinalPrice && freshFinalPrice) {
                currentFinalPrice.innerHTML = freshFinalPrice.innerHTML;
                currentFinalPrice.dataset.hasCompareAt = 'true';
              }
            }
          });
        }
        
        // Trigger cart subtotal update
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Force update the strikethrough pricing (debounced)
        if (typeof updateStrikethroughPricing === 'function') {
          updateStrikethroughPricing();
        }
        
        // Also update the cart subtotal section from fresh HTML
        const cartDetailsSection = tempDiv.querySelector('.cart__details');
        if (cartDetailsSection) {
          const freshSubtotal = cartDetailsSection.querySelector('.cart__total-prices');
          const currentSubtotal = document.querySelector('.cart__total-prices');
          if (freshSubtotal && currentSubtotal) {
            currentSubtotal.innerHTML = freshSubtotal.innerHTML;
          }
        }
      })
      .catch(error => {
        console.error('Error refreshing cart prices:', error);
      });
  }
}
customElements.define('cart-items', CartItems);

class CartItem extends HTMLElement {
  constructor() {
    super();

    // TODO: Make debounce standalone module
    const debounce = (f, delay) => {
      let timer = 0;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => f.apply(this, args), delay);
      };
    };

    this._debouncedChangeQuantity = debounce(
      this._changeQuantity.bind(this),
      250,
    );
    this._boundRemoveProduct = this._removeProduct.bind(this);
  }

  connectedCallback() {
    this.key = this.getAttribute('key');
    this.item = window.theme.cart.store
      .getState()
      .products.items.find((item) => item.key === this.key);

    if (!this.item) return;

    if (
      this.item.variant_id ===
      window.theme.cart.store.getState().giftWrapping.productId
    ) {
      this.giftWrappingItem = true;
      this.noQuantityInput = true;
      this.noLinks = true;
      this.giftWrappingMessageEnabled =
        window.theme.cart.store.getState().giftWrapping.giftMessageEnabled;
      this.giftWrappingMessage =
        window.theme.cart.store.getState().products.attributes[
          'gift-wrapping-message'
        ] || '';
    }

    this.image = this.querySelector('cart-item-image-container img');
    this.imageContainerRatio = this.getAttribute(
      'image-container-ratio',
    ).trim();
    this.imageFit = this.hasAttribute('image-fit');

    this.details = this.querySelector('cart-item-details');

    this._isRendered = this.hasAttribute('rendered');

    if (!this._isRendered) {
      this._render();
      // Check for compare-at prices immediately after render for new items
      setTimeout(() => {
        const hasCompareAt = this.querySelector('cart-item-original-line-price')?.dataset.hasCompareAt === 'true';
        if (!hasCompareAt && this.item) {
          // Fetch and display compare-at prices for this new item
          this._fetchAndDisplayCompareAtPrices();
        }
      }, 100);
    }

    this.quantity = this.querySelector('quantity-input');
    this.removeItemButton = this.querySelector('[cart-item-remove');

    setTimeout(() => {
      if (this.quantity)
        this.quantity.addEventListener('update', this._debouncedChangeQuantity);
      if (this.removeItemButton)
        this.removeItemButton.addEventListener(
          'click',
          this._boundRemoveProduct,
        );
    });

    this.unsubscribe = window.theme.cart.store.subscribe((state, prevState) => {
      if (state.lineItemsBeingUpdated.length > 0) {
        this._renderError();
      }

      if (state.lineItemsBeingUpdated.includes(this.key)) {
        this._toggleSpinner(true);
      } else {
        const newItem = state.products.items.find(
          (item) => item.key === this.key,
        );

        if (newItem && prevState.lineItemsBeingUpdated.includes(this.key)) {
          this._toggleSpinner(false);
        }

        if (!state.lineItemsBeingUpdated.includes(this.key)) {
          if (this.quantity && this.quantity.value !== newItem.quantity) {
            this.quantity.value = newItem.quantity;
          } else {
            const quantityDisplay = this.querySelector('quantity-display');

            if (quantityDisplay) {
              quantityDisplay.innerHTML = newItem.quantity;
            }
          }
        }

        this.item = newItem;

        // Don't clear if we have compare-at prices - just update them
        const hasCompareAt = this.querySelector('cart-item-original-line-price')?.dataset.hasCompareAt === 'true';
        if (hasCompareAt) {
          this._updateCompareAtPricesQuickly();
        } else {
          this._clearTotalPrice();
          this._renderTotalPriceAndDiscounts();
        }
      }
    });
  }

  disconnectedCallback() {
    this.unsubscribe();

    if (this.quantity)
      this.quantity.removeEventListener(
        'update',
        this._debouncedChangeQuantity,
      );

    if (this.removeItemButton)
      this.removeItemButton.removeEventListener(
        'click',
        this._boundRemoveProduct,
      );
  }

  _render() {
    this.querySelectorAll('a[cart-item-url]').forEach((link) => {
      if (this.noLinks) {
        const div = document.createElement('div');
        if (link.hasAttribute('class')) {
          div.setAttribute('class', link.getAttribute('class'));
        }
        if (link.hasAttribute('style')) {
          div.setAttribute('style', link.getAttribute('style'));
        }
        div.innerHTML = link.innerHTML;
        link.replaceWith(div);
        this.image = this.querySelector('cart-item-image-container img');
      } else {
        link.href = this.item.url;
      }
    });

    this._setImage();

    if (this.imageContainerRatio === 'natural') {
      this.image.closest('.o-ratio').style.paddingBottom = `${
        (1 /
          (this.item.featured_image
            ? this.item.featured_image.aspect_ratio
            : 1)) *
        100
      }%`;
    } else {
      this.image.closest('.o-ratio').style.paddingBottom = null;
    }

    this._updateSkeleton();

    this.querySelector('cart-item-title').innerHTML = this.item.product_title;

    const metaItemTemplate = document.getElementById(
      'template-cart-item-meta',
    ).content;

    const vendor = this.querySelector('cart-item-vendor');
    if (vendor) {
      vendor.appendChild(metaItemTemplate.cloneNode(true));
      vendor.querySelector('property-value').innerHTML = this.item.vendor;
    }

    if (
      this.item.variant_title &&
      !this.item.variant_title.includes('Default')
    ) {
      const defaultProperty = this.querySelector('cart-item-default-property');
      defaultProperty.appendChild(metaItemTemplate.cloneNode(true));
      defaultProperty.querySelector('property-value').innerHTML =
        this.item.variant_title;
    }

    if (Object.keys(this.item.properties).length > 0) {
      const propertyItemTemplate = document.getElementById(
        'template-cart-item-property',
      ).content;
      const properties = this.querySelector('cart-item-properties');
      Object.entries(this.item.properties).forEach(([name, value]) => {
        if (value !== '' && name.slice(0, 1) !== '_') {
          const propertyTemplate = propertyItemTemplate.cloneNode(true);
          propertyTemplate.querySelector('property-name').innerHTML = name;
          propertyTemplate.querySelector('property-value').innerHTML =
            value.includes('/uploads/')
              ? `<a href="${value}">${value.split('/').pop()}</a>`
              : value;
          properties.appendChild(propertyTemplate);
        }
      });
    }

    if (this.item.selling_plan_allocation) {
      const sellingPlanAllocation = this.querySelector(
        'cart-item-selling-plan-allocation',
      );
      sellingPlanAllocation.appendChild(metaItemTemplate.cloneNode(true));

      sellingPlanAllocation.querySelector('property-value').innerHTML = `${
        this.item.selling_plan_allocation.selling_plan.name
      }${
        this.item.selling_plan_allocation.compare_at_price &&
        this.item.selling_plan_allocation.compare_at_price !==
          this.item.selling_plan_allocation.price
          ? ` (-${Math.round(
              (1 -
                this.item.selling_plan_allocation.price /
                  this.item.selling_plan_allocation.compare_at_price) *
                100,
            )}%)`
          : ''
      }`;
    }

    const sku = this.querySelector('cart-item-sku');
    if (sku && this.item.sku) {
      sku.appendChild(metaItemTemplate.cloneNode(true));
      sku.querySelector('property-value').innerHTML = this.item.sku;
    }

    if (this.item.unit_price_measurement) {
      const itemUnitPrice = this.querySelector('cart-item-unit-price');
      const unitPriceTemplate = document.getElementById(
        'template-unit-price',
      ).content;
      itemUnitPrice.appendChild(unitPriceTemplate.cloneNode(true));

      const baseUnit = `${
        this.item.unit_price_measurement.reference_value !== 1
          ? this.item.unit_price_measurement.reference_value
          : ''
      }${this.item.unit_price_measurement.reference_unit}`;

      itemUnitPrice.querySelector('unit-price').innerHTML =
        window.Shopify.formatMoney(
          this.item.unit_price,
          window.theme.money_format,
        );
      itemUnitPrice.querySelector('base-unit').innerHTML = baseUnit;
    }

    if (this.giftWrappingItem && this.giftWrappingMessageEnabled) {
      const giftWrappingMessageTemplate = document.getElementById(
        'template-cart-gift-wrapping-message',
      ).content;
      this.querySelector('cart-item-micro-copy').appendChild(
        giftWrappingMessageTemplate.cloneNode(true),
      );

      this.classList.add('cart-item--full-details');

      this.querySelector('cart-textarea').value = this.giftWrappingMessage;
    }

    if (this.noQuantityInput) {
      const quantityDisplay = document.createElement('quantity-display');
      quantityDisplay.classList.add('cart-item__qty-display');
      quantityDisplay.innerHTML = this.item.quantity;
      this.querySelector('cart-item-quantity').appendChild(quantityDisplay);
    } else {
      const quantityInputTemplate = document.getElementById(
        'template-quantity-input',
      ).content;
      const quantityElement = quantityInputTemplate.cloneNode(true);
      this.quantity = quantityElement.querySelector('quantity-input');
      const quantityInput = quantityElement.querySelector(
        'input[type="number"]',
      );
      quantityInput.setAttribute('id', `updates_${this.item.key}`);
      quantityInput.setAttribute('name', `updates[]`);
      this.querySelector('cart-item-quantity').appendChild(this.quantity);

      setTimeout(() => {
        this.quantity.value = this.item.quantity;
      });

      this.removeItemButton = this.querySelector('button[cart-item-remove]');
    }

    this._renderTotalPriceAndDiscounts();

    this.setAttribute('rendered', '');
  }

  // _setImage(url, width, height, alt = '', srcsetWidths = [120, 240, 360, 480]) {
  _setImage(srcsetWidths = [120, 240, 360, 480]) {
    if (!this.image) return;

    const imageData = this.item.featured_image;

    if (imageData && imageData.url) {
      const { width, height, url } = imageData;

      const imageContainerRatio =
        this.imageContainerRatio !== 'natural'
          ? this.imageContainerRatio
              .split(':')
              .reduce(
                (ratio, value) => (ratio !== 0 ? value / ratio : value),
                0,
              )
          : null;
      const aspectRatio = width / height;
      const uncroppedImage =
        this.imageContainerRatio === 'natural' ||
        (imageContainerRatio && this.imageFit);

      let masterWidth = width;
      let masterHeight = height;

      if (!uncroppedImage) {
        masterHeight = Math.round(masterWidth * imageContainerRatio);

        if (masterHeight > height) {
          masterHeight = height;
          masterWidth = Math.round(masterHeight / imageContainerRatio);
        }
      }

      const srcset = srcsetWidths.reduce((srcset, srcWidth) => {
        const srcHeight = Math.round(
          uncroppedImage
            ? srcWidth / aspectRatio
            : srcWidth * imageContainerRatio,
        );

        if (srcWidth > masterWidth || srcHeight > masterHeight) return srcset;

        return `${srcset}${url}${
          url.includes('?') ? '&' : '?'
        }width=${srcWidth}${
          !uncroppedImage ? `&height=${srcHeight}&crop=center` : ''
        } ${srcWidth}w ${srcHeight}h, `;
      }, '');

      const masterSrc = !uncroppedImage
        ? `${url}${
            url.includes('?') ? '&' : '?'
          }width=${masterWidth}&height=${masterHeight}&crop=center`
        : url;

      this.image.setAttribute('src', masterSrc);
      this.image.setAttribute('srcset', srcset);
      this.image.setAttribute('width', masterWidth);
      this.image.setAttribute('height', masterHeight);
    }

    this.image.setAttribute('alt', `Image for ${this.item.title}`);
  }

  _updateSkeleton() {
    if (!this.image) return;

    const skeleton =
      this.image.nextElementSibling &&
      this.image.nextElementSibling.tagName.toLowerCase() === 'image-skeleton'
        ? this.image.nextElementSibling
        : null;

    if (skeleton) {
      const svg = skeleton.querySelector('svg');
      const rect = svg.querySelector('rect');
      const width = this.image.getAttribute('width');
      const height = this.image.getAttribute('height');
      skeleton.setAttribute(
        'aria-label',
        `Loading image for ${this.item.title}`,
      );
      svg.setAttribute('width', width);
      svg.setAttribute('height', height);
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

      rect.setAttribute('width', width);
      rect.setAttribute('height', height);
    }
  }

  _toggleSpinner(visible = true) {
    if (visible) {
      this._clearTotalPrice();

      const quantityInputTemplate =
        document.getElementById('template-spinner').content;
      this.querySelector('cart-item-spinner').appendChild(
        quantityInputTemplate.cloneNode(true),
      );
    } else {
      this.querySelector('cart-item-spinner').innerHTML = '';
    }
  }

  _clearTotalPrice() {
    const originalLinePrice = this.querySelector('cart-item-original-line-price');
    const priceContainer = originalLinePrice.parentElement;
    
    // Don't clear if Liquid has already rendered compare-at price
    const hasCompareAt = originalLinePrice.dataset.hasCompareAt === 'true';
    if (hasCompareAt) {
      return;
    }

    if (priceContainer) {
      const priceContainerWidth = priceContainer.offsetWidth;
      const priceContainerHeight = priceContainer.offsetHeight;
      priceContainer.style.width = `${priceContainerWidth}px`;
      priceContainer.style.height = `${priceContainerHeight}px`;
    }

    this.querySelector('cart-item-original-line-price').innerHTML = '';
    this.querySelector('cart-item-final-line-price').innerHTML = '';
    // this.querySelector('cart-item-discounts').setAttribute('hidden', '');
  }

  _renderTotalPriceAndDiscounts() {
    const originalLinePrice = this.querySelector(
      'cart-item-original-line-price',
    );
    const finalLinePrice = this.querySelector('cart-item-final-line-price');
    
    // Check if Liquid has already rendered compare-at price
    const hasCompareAt = originalLinePrice.dataset.hasCompareAt === 'true';
    if (hasCompareAt) {
      // Update the compare-at prices with new quantity immediately
      this._updateCompareAtPricesQuickly();
      return;
    }
    
    const priceTemplate = document.getElementById('template-price').content;

    // Only handle cart-level discounts since compare_at_price doesn't exist in JS cart API
    if (this.item.original_line_price !== this.item.final_line_price) {
      // Cart-level discount
      originalLinePrice.appendChild(priceTemplate.cloneNode(true));
      originalLinePrice
        .querySelector('.price')
        .classList.add('price--original');
      originalLinePrice.querySelector('money-amount').innerHTML =
        window.Shopify.formatMoney(
          this.item.original_line_price,
          window.theme.money_format,
        );
      finalLinePrice.appendChild(priceTemplate.cloneNode(true));
      finalLinePrice.querySelector('.price').classList.add('price--sale');
      finalLinePrice.querySelector('money-amount').innerHTML =
        window.Shopify.formatMoney(
          this.item.final_line_price,
          window.theme.money_format,
        );
    } else {
      // No discount
      originalLinePrice.appendChild(priceTemplate.cloneNode(true));
      originalLinePrice.querySelector('money-amount').innerHTML =
        window.Shopify.formatMoney(
          this.item.final_line_price,
          window.theme.money_format,
        );
    }

    const discountsContainer = this.querySelector('cart-item-discounts');
    const discounts = discountsContainer.querySelector('discount-list');
    const discountItems = JSON.stringify(
      this.item.line_level_discount_allocations.reduce(
        (items, { amount, discount_application: { key, title } }) => {
          items.push({
            key,
            title,
            amount,
          });

          return items;
        },
        [],
      ),
    );
    discounts.setAttribute('items', discountItems);
    // discountsContainer.removeAttribute('hidden');

    this.classList.toggle(
      'has-discounts',
      this.item.line_level_discount_allocations.length > 0,
    );

    const priceContainer = this.querySelector(
      'cart-item-original-line-price',
    ).parentElement;

    if (priceContainer) {
      priceContainer.style.width = '';
      priceContainer.style.height = '';
    }
  }

  _renderError(errorText = '') {
    const errorContainer = this.querySelector('cart-item-errors');

    this.classList.toggle('has-errors', errorText);
    errorContainer.innerHTML = `<div class="errors qty-error u-small">${errorText}</div>`;
  }

  async _changeQuantity() {
    if (!this.key) return;

    try {
      await window.theme.cart.store
        .getState()
        .change(this.key, this.quantity.value);
      
      // Refresh cart HTML to get compare-at prices after quantity change
      setTimeout(() => this._refreshCartPrices(), 500);
    } catch (e) {
      if (e.name && e.name === 'AbortError') return;
      this._renderError(e.message);
    }
  }
  
  _updateCompareAtPricesQuickly() {
    // Quick update for compare-at prices when quantity changes
    // Use the stored compare-at price and update with new quantity
    const originalLinePrice = this.querySelector('cart-item-original-line-price');
    const finalLinePrice = this.querySelector('cart-item-final-line-price');
    const compareAtPrice = parseInt(originalLinePrice.dataset.compareAtPrice || '0');
    
    if (compareAtPrice > 0 && this.item) {
      // Calculate the item's price per unit
      const itemPricePerUnit = this.item.final_line_price / this.item.quantity;
      
      const compareLinePrice = compareAtPrice * this.item.quantity;
      const actualLinePrice = this.item.final_line_price;
      
      // Check if the displayed prices would be the same
      // If compare-at total equals actual total, don't show strikethrough
      const priceDifference = Math.abs(compareLinePrice - actualLinePrice);
      
      // Only show strikethrough if there's a meaningful price difference (more than $0.01)
      if (priceDifference > 1) { // More than 1 cent difference
        // Update both elements with new calculated values
        originalLinePrice.innerHTML = `
          <div class="price price--original">
            <span class="money"><money-amount>${window.Shopify.formatMoney(compareLinePrice, window.theme.money_format)}</money-amount></span>
          </div>
        `;
        
        finalLinePrice.innerHTML = `
          <div class="price price--sale">
            <span class="money"><money-amount>${window.Shopify.formatMoney(actualLinePrice, window.theme.money_format)}</money-amount></span>
          </div>
        `;
        
        // Also trigger subtotal update
        if (typeof updateStrikethroughPricing === 'function') {
          updateStrikethroughPricing();
        }
      } else if (this.item.original_line_price !== this.item.final_line_price) {
        // Compare-at equals price, but there's a cart-level discount
        originalLinePrice.innerHTML = `
          <div class="price price--original">
            <span class="money"><money-amount>${window.Shopify.formatMoney(this.item.original_line_price, window.theme.money_format)}</money-amount></span>
          </div>
        `;
        finalLinePrice.innerHTML = `
          <div class="price price--sale">
            <span class="money"><money-amount>${window.Shopify.formatMoney(this.item.final_line_price, window.theme.money_format)}</money-amount></span>
          </div>
        `;
        
        if (typeof updateStrikethroughPricing === 'function') {
          updateStrikethroughPricing();
        }
      } else {
        // No strikethrough - prices are equal and no discounts
        originalLinePrice.innerHTML = '';
        finalLinePrice.innerHTML = `
          <div class="price">
            <span class="money"><money-amount>${window.Shopify.formatMoney(this.item.final_line_price, window.theme.money_format)}</money-amount></span>
          </div>
        `;
      }
    } else if (this.item && this.item.original_line_price !== this.item.final_line_price) {
      // No compare-at price but there's a discount, show it
      originalLinePrice.innerHTML = `
        <div class="price price--original">
          <span class="money"><money-amount>${window.Shopify.formatMoney(this.item.original_line_price, window.theme.money_format)}</money-amount></span>
        </div>
      `;
      finalLinePrice.innerHTML = `
        <div class="price price--sale">
          <span class="money"><money-amount>${window.Shopify.formatMoney(this.item.final_line_price, window.theme.money_format)}</money-amount></span>
        </div>
      `;
      
      if (typeof updateStrikethroughPricing === 'function') {
        updateStrikethroughPricing();
      }
    }
  }
  
  _fetchAndDisplayCompareAtPrices() {
    // Fetch compare-at prices for newly rendered items
    if (!this.item) return;
    
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartItem = cart.items.find(i => i.key === this.key);
        if (!cartItem) return;
        
        // Check if item has compare_at_price AND it's greater than the actual price
        if (cartItem.compare_at_price && cartItem.compare_at_price > cartItem.price) {
          const originalLinePrice = this.querySelector('cart-item-original-line-price');
          const finalLinePrice = this.querySelector('cart-item-final-line-price');
          
          if (!originalLinePrice || !finalLinePrice) return;
          
          const compareLinePrice = cartItem.compare_at_price * cartItem.quantity;
          // Use final_line_price which includes all discounts
          const actualLinePrice = cartItem.final_line_price;
          
          // Clear existing content and add compare-at pricing
          originalLinePrice.innerHTML = `
            <div class="price price--original">
              <span class="money"><money-amount>${window.Shopify.formatMoney(compareLinePrice, window.theme.money_format)}</money-amount></span>
            </div>
          `;
          originalLinePrice.dataset.hasCompareAt = 'true';
          originalLinePrice.dataset.compareAtPrice = cartItem.compare_at_price;
          
          finalLinePrice.innerHTML = `
            <div class="price price--sale">
              <span class="money"><money-amount>${window.Shopify.formatMoney(actualLinePrice, window.theme.money_format)}</money-amount></span>
            </div>
          `;
          finalLinePrice.dataset.hasCompareAt = 'true';
          
          // Also trigger subtotal update
          if (typeof updateStrikethroughPricing === 'function') {
            updateStrikethroughPricing();
          }
        }
      })
      .catch(error => {
        console.error('Error fetching compare-at prices:', error);
      });
  }
  
  _refreshCartPrices() {
    // Fetch fresh cart HTML to get compare-at price data
    fetch('/cart?sections=cart-drawer,main-cart')
      .then(response => response.json())
      .then(sections => {
        // Parse the HTML and update prices for items with compare-at prices
        const tempDiv = document.createElement('div');
        const sectionHtml = sections['cart-drawer'] || sections['main-cart'];
        if (sectionHtml) {
          tempDiv.innerHTML = sectionHtml;
          
          // Find all cart items with compare-at prices in the fresh HTML
          const freshItems = tempDiv.querySelectorAll('cart-item');
          freshItems.forEach(freshItem => {
            const key = freshItem.getAttribute('key');
            if (!key) return;
            
            // Find the corresponding item in the current DOM
            const currentItem = document.querySelector(`cart-item[key="${key}"]`);
            if (!currentItem) return;
            
            // Check if fresh item has compare-at pricing
            const freshOriginalPrice = freshItem.querySelector('cart-item-original-line-price');
            const freshFinalPrice = freshItem.querySelector('cart-item-final-line-price');
            
            if (freshOriginalPrice && freshOriginalPrice.dataset.hasCompareAt === 'true') {
              // Replace the price elements with the fresh ones that have compare-at prices
              const currentOriginalPrice = currentItem.querySelector('cart-item-original-line-price');
              const currentFinalPrice = currentItem.querySelector('cart-item-final-line-price');
              
              if (currentOriginalPrice && freshOriginalPrice) {
                currentOriginalPrice.innerHTML = freshOriginalPrice.innerHTML;
                currentOriginalPrice.dataset.hasCompareAt = 'true';
                currentOriginalPrice.dataset.compareAtPrice = freshOriginalPrice.dataset.compareAtPrice;
              }
              
              if (currentFinalPrice && freshFinalPrice) {
                currentFinalPrice.innerHTML = freshFinalPrice.innerHTML;
                currentFinalPrice.dataset.hasCompareAt = 'true';
              }
              
              // Call the update method on this specific cart item after refresh
              if (currentItem && currentItem._updateCompareAtPricesQuickly) {
                currentItem._updateCompareAtPricesQuickly();
              }
            }
          });
        }
        
        // Also trigger the cart subtotal update
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Force update the strikethrough pricing (debounced)
        if (typeof updateStrikethroughPricing === 'function') {
          updateStrikethroughPricing();
        }
        
        // Also update the cart subtotal section from fresh HTML
        const cartDetailsSection = tempDiv.querySelector('.cart__details');
        if (cartDetailsSection) {
          const freshSubtotal = cartDetailsSection.querySelector('.cart__total-prices');
          const currentSubtotal = document.querySelector('.cart__total-prices');
          if (freshSubtotal && currentSubtotal) {
            currentSubtotal.innerHTML = freshSubtotal.innerHTML;
          }
        }
      })
      .catch(error => {
        console.error('Error refreshing cart prices:', error);
      });
  }

  async _removeProduct(e) {
    if (!this.key) return;

    e.preventDefault();

    if (this.giftWrappingItem) {
      await window.theme.cart.store.getState().setGiftWrapping(false);
    } else {
      await window.theme.cart.store.getState().change(this.key, 0);
    }
  }
}
customElements.define('cart-item', CartItem);

class DiscountList extends HTMLElement {
  constructor() {
    super();

    // [{key: 'unique discount key', title: 'discount title', amount: 'discount amount'}]
    this.items = JSON.parse(this.getAttribute('items') || '[]');
  }

  static get observedAttributes() {
    return ['items'];
  }

  attributeChangedCallback(_, oldVal, newVal) {
    if (oldVal !== newVal) {
      this.items = JSON.parse(newVal);
      this._renderDiscounts();
    }
  }

  _renderDiscounts() {
    this.innerHTML = '';
    if (this.items.length > 0) {
      const discountsList = document.createElement('ul');
      discountsList.classList.add('discounts');
      this.appendChild(discountsList);

      this.items.forEach((item) => {
        const discount = document.createElement('li');
        discount.classList.add('discount');
        discountsList.appendChild(discount);
        const discountTemplate = document.getElementById(
          'template-discount-item',
        ).content;
        discount.appendChild(discountTemplate.cloneNode(true));
        discount.querySelector('discount-title').innerHTML = item.title;
        if (item.amount > 0) {
          discount.querySelector('discount-amount').innerHTML =
            window.Shopify.formatMoney(item.amount, window.theme.money_format);
        }
      });
    }
  }
}
customElements.define('discount-list', DiscountList);

class CartTextarea extends HTMLElement {
  constructor() {
    super();
  }

  async connectedCallback() {
    this._text = this.querySelector('textarea');
    this._status = this.querySelector('cart-textarea-status');

    this._updatedHTML = '<div class="is-saved">&checkmark;</div>';

    this._updating = false;
    this._updated = false;

    this._spinnerTemplate = document.getElementById('template-spinner').content;

    this._function = () => {};

    this._text.addEventListener('input', this._function);
  }

  disconnectedCallback() {
    this._text.removeEventListener('input', this._function);
  }

  set updating(isUpdating) {
    this._updated = false;
    this._updating = isUpdating;
    if (isUpdating) {
      this._status.innerHTML = '';
      this._status.appendChild(this._spinnerTemplate.cloneNode(true));
    }
  }

  get updating() {
    return this._updating;
  }

  set updated(isUpdated) {
    this._updating = false;
    this._updated = isUpdated;
    if (isUpdated) {
      this._status.innerHTML = this._updatedHTML;
    }
  }

  get updated() {
    return this._updated;
  }

  set value(value) {
    this._text.value = value;
  }

  get value() {
    return this._text.value;
  }

  set updatedHTML(html) {
    this._updatedHTML = html;
  }

  get updatedHTML() {
    return this._updatedHTML;
  }

  set function(f) {
    const debounce = (f, delay) => {
      let timer = 0;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => f.apply(this, args), delay);
      };
    };

    this._text.removeEventListener('input', this._function);

    this._function = debounce(f, 1000);

    this._text.addEventListener('input', this._function);
  }

  get function() {
    return this._function;
  }
}
customElements.define('cart-textarea', CartTextarea);

class CartGiftWrappingMessage extends HTMLElement {
  constructor() {
    super();
  }

  async connectedCallback() {
    this._cartTextarea = this.querySelector('cart-textarea');

    setTimeout(() => {
      this._cartTextarea.function = this._updateMessage.bind(this);
    });

    this.unsubscribe = window.theme.cart.store.subscribe((state, prevState) => {
      if (state.giftWrapping.messageBeingUpdated) {
        this._cartTextarea.updating = true;
      } else if (prevState.giftWrapping.messageBeingUpdated) {
        this._cartTextarea.updated = true;
      }
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  async _updateMessage(e) {
    // try {
    await window.theme.cart.store
      .getState()
      .updateGiftWrappingMessage(e.target.value);
    // } catch (e) {
    //   if (e.name && e.name === 'AbortError') return;
    //   console.log(e);
    // }
  }
}
customElements.define('cart-gift-wrapping-message', CartGiftWrappingMessage);

class CartNote extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this._cartTextarea = this.querySelector('cart-textarea');

    setTimeout(() => {
      this._cartTextarea.function = this._updateNote.bind(this);
    });

    this.unsubscribe = window.theme.cart.store.subscribe((state, prevState) => {
      if (state.noteBeingUpdated) {
        this._cartTextarea.updating = true;
      } else if (prevState.noteBeingUpdated) {
        this._cartTextarea.updated = true;
      }

      // if (state.products.note !== prevState.products.note) {
      //   this.text.value = state.products.note;
      // }
    });
  }

  disconnectedCallback() {
    // this.text.removeEventListener('input', this._debouncedUpdateNote);
    this.unsubscribe();
  }

  async _updateNote(e) {
    try {
      await window.theme.cart.store.getState().updateNote(e.target.value);
    } catch (e) {
      if (e.name && e.name === 'AbortError') return;
      console.log(e);
    }
  }

  // set value(value) {
  //   this.text.value = value;
  // }

  // get value() {
  //   return this.text.value;
  // }
}
customElements.define('cart-note', CartNote);

class CartSpinner extends HTMLElement {
  constructor() {
    super();
  }
}
customElements.define('cart-spinner', CartSpinner);

class CartTerms extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.closest('form');
    this.input = this.querySelector('input[type="checkbox"]');
    this.error = this.querySelector('cart-terms-error');

    this._boundValidateForm = this._validateForm.bind(this);
    this._boundInputChange = this._inputChange.bind(this);

    setTimeout(() => {
      if (window.theme.lib.Cookies.get('cart_order_terms') === 'accepted') {
        this.input.checked = true;
      }
      this.form.addEventListener('submit', this._boundValidateForm);
      this.input.addEventListener('change', this._boundInputChange);
    });
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this._boundValidateForm);
    this.input.removeEventListener('change', this._boundInputChange);
  }

  _validateForm(e) {
    if (!this.input.checked) {
      e.preventDefault();
      this._toggleError(true);
    }
  }

  _inputChange({ target: { checked } }) {
    if (checked) {
      this._toggleError(false);
    }
    window.theme.lib.Cookies.set(
      'cart_order_terms',
      checked ? 'accepted' : '',
      { secure: true, SameSite: 'None' },
    );
  }

  _toggleError(visible = true) {
    if (visible) {
      this.error.removeAttribute('hidden');
      this.error.classList.add('is-active');
    } else {
      this.error.classList.remove('is-active');
      this.error.setAttribute('hidden', '');
    }
  }
}
customElements.define('cart-terms', CartTerms);

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.timeout = null;

    this._boundAutoClose = this._autoClose.bind(this);
    this._boundStopAutoClose = this._stopAutoClose.bind(this);
  }

  connectedCallback() {
    this.image = this.querySelector('cart-item-image-container img');
    this.imageContainerRatio = this.querySelector('.cart-item')
      .getAttribute('image-container-ratio')
      .trim();
    this.imageFit = this.querySelector('.cart-item').hasAttribute('image-fit');

    this.item = window.theme.cart.store.getState().latestAddedProduct;
    this.unsubscribe = window.theme.cart.store.subscribe((state) => {
      if (state.latestAddedProduct) {
        this.item = state.latestAddedProduct;
        this._render();
      } else {
        this.item = null;
      }
    });

    if (this.closest('.mfp-content')) {
      this._autoClose();
    }
  }

  disconnectedCallback() {
    this.unsubscribe();

    this.removeEventListener('mouseenter', this._boundStopAutoClose);
    this.removeEventListener('mouseleave', this._boundAutoClose);
  }

  _autoClose() {
    this.timeout = setTimeout(() => {
      $.magnificPopup.close();
    }, 4000);
    this.addEventListener('mouseenter', this._boundStopAutoClose, {
      once: true,
    });
  }

  _stopAutoClose() {
    if (this.timeout) clearTimeout(this.timeout);
    this.addEventListener('mouseleave', this._boundAutoClose, {
      once: true,
    });
  }

  _render() {
    if (!this.item) return;

    this._setImage();

    if (this.imageContainerRatio === 'natural') {
      this.image.closest('.o-ratio').style.paddingBottom = `${
        (1 /
          (this.item.featured_image
            ? this.item.featured_image.aspect_ratio
            : 1)) *
        100
      }%`;
    } else {
      this.image.closest('.o-ratio').style.paddingBottom = null;
    }

    this._updateSkeleton();

    this.querySelector('cart-item-title').innerHTML = this.item.product_title;

    const metaItemTemplate = document.getElementById(
      'template-cart-item-meta',
    ).content;

    if (
      this.item.variant_title &&
      !this.item.variant_title.includes('Default')
    ) {
      const defaultProperty = this.querySelector('cart-item-default-property');
      defaultProperty.innerHTML = '';
      defaultProperty.appendChild(metaItemTemplate.cloneNode(true));
      defaultProperty.querySelector('property-value').innerHTML =
        this.item.variant_title;
    }

    const vendor = this.querySelector('cart-item-vendor');
    if (vendor) {
      vendor.innerHTML = '';
      vendor.appendChild(metaItemTemplate.cloneNode(true));
      vendor.querySelector('property-value').innerHTML = this.item.vendor;
    }
  }

  _setImage(srcsetWidths = [120, 240, 360, 480]) {
    if (!this.image) return;

    const imageData = this.item.featured_image;

    if (imageData && imageData.url) {
      const { width, height, url } = imageData;

      const imageContainerRatio =
        this.imageContainerRatio !== 'natural'
          ? this.imageContainerRatio
              .split(':')
              .reduce(
                (ratio, value) => (ratio !== 0 ? value / ratio : value),
                0,
              )
          : null;
      const aspectRatio = width / height;
      const uncroppedImage =
        this.imageContainerRatio === 'natural' ||
        (imageContainerRatio && this.imageFit);

      let masterWidth = width;
      let masterHeight = height;

      if (!uncroppedImage) {
        masterHeight = Math.round(masterWidth * imageContainerRatio);

        if (masterHeight > height) {
          masterHeight = height;
          masterWidth = Math.round(masterHeight / imageContainerRatio);
        }
      }

      const srcset = srcsetWidths.reduce((srcset, srcWidth) => {
        const srcHeight = Math.round(
          uncroppedImage
            ? srcWidth / aspectRatio
            : srcWidth * imageContainerRatio,
        );

        if (srcWidth > masterWidth || srcHeight > masterHeight) return srcset;

        return `${srcset}${url}${
          url.includes('?') ? '&' : '?'
        }width=${srcWidth}${
          !uncroppedImage ? `&height=${srcHeight}&crop=center` : ''
        } ${srcWidth}w ${srcHeight}h, `;
      }, '');

      const masterSrc = !uncroppedImage
        ? `${url}${
            url.includes('?') ? '&' : '?'
          }width=${masterWidth}&height=${masterHeight}&crop=center`
        : url;

      this.image.setAttribute('src', masterSrc);
      this.image.setAttribute('srcset', srcset);
      this.image.setAttribute('width', masterWidth);
      this.image.setAttribute('height', masterHeight);
    }

    this.image.setAttribute('alt', `Image for ${this.item.title}`);
  }

  _updateSkeleton() {
    if (!this.image) return;

    const skeleton =
      this.image.nextElementSibling &&
      this.image.nextElementSibling.tagName.toLowerCase() === 'image-skeleton'
        ? this.image.nextElementSibling
        : null;

    if (skeleton) {
      const svg = skeleton.querySelector('svg');
      const rect = svg.querySelector('rect');
      const width = this.image.getAttribute('width');
      const height = this.image.getAttribute('height');
      skeleton.setAttribute(
        'aria-label',
        `Loading image for ${this.item.title}`,
      );
      svg.setAttribute('width', width);
      svg.setAttribute('height', height);
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

      rect.setAttribute('width', width);
      rect.setAttribute('height', height);
    }
  }
}
customElements.define('cart-notification', CartNotification);

class CartQuantityInfo extends HTMLElement {
  constructor() {
    super();

    this.itemsCount = this.querySelector('items-count');
    this.infoContent = this.querySelector('info-content');
  }

  connectedCallback() {
    this.unsubscribe = window.theme.cart.store.subscribe((state) => {
      if (state.products.item_count > 0) {
        this.removeAttribute('hidden');

        if (state.products.item_count == 1) {
          this.infoContent.innerHTML = window.theme.t.cart_item_singular;
        } else {
          this.infoContent.innerHTML = window.theme.t.cart_item_plural;
        }
      } else {
        this.setAttribute('hidden', '');
      }
      this.itemsCount.innerHTML = state.products.item_count;
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }
}
customElements.define('cart-quantity-info', CartQuantityInfo);

class CartGiftWrappingBanner extends HTMLElement {
  constructor() {
    super();

    this._boundEnable = this._enable.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('button');

    if (
      Boolean(
        window.theme.cart.store.getState().products.attributes['gift-wrapping'],
      )
    ) {
      this.setAttribute('hidden', '');
    }

    this.unsubscribe = window.theme.cart.store.subscribe((state) => {
      const giftWrappingSet = Boolean(
        state.products.attributes['gift-wrapping'],
      );

      if (!giftWrappingSet) {
        this.removeAttribute('hidden');
      }
    });

    this.trigger.addEventListener('click', this._boundEnable);
  }

  disconnectedCallback() {
    this.unsubscribe();

    this.trigger.removeEventListener('click', this._boundEnable);
  }

  async _enable(e) {
    e.preventDefault();

    try {
      await this._setIsDoing();
      await window.theme.cart.store.getState().setGiftWrapping();
      await this._setIsDoing(false);
      this.setAttribute('hidden', '');
    } catch (error) {
      console.log(error);
      await this._setIsDoing(false, true);
    }
  }

  _setIsDoing(isDoing = true, error = false) {
    return new Promise((resolve) => {
      if (!this.trigger) {
        resolve('No trigger');
        return;
      }

      if (error) {
        this.trigger.classList.remove('is-doing');
        resolve('error');
        return;
      }

      if (isDoing) {
        this.trigger.classList.remove('is-done');
        this.trigger.classList.add('is-doing');
        resolve('Doing');
      } else {
        this.trigger.classList.remove('is-doing');
        this.trigger.classList.add('is-done');

        this.trigger.addEventListener('animationend', () => {
          this.trigger.classList.remove('is-done');
          resolve('Done');
        });
      }
      return;
    });
  }
}
customElements.define('cart-gift-wrapping-banner', CartGiftWrappingBanner);

class CartPromoCode {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    // Setup handlers immediately if DOM is already loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupPromoCodeHandlers();
        this.updateAppliedCodesDisplay();
        this.observeForPromoForm();
      });
    } else {
      // DOM is already loaded, setup immediately
      setTimeout(() => {
        this.setupPromoCodeHandlers();
        this.updateAppliedCodesDisplay();
        this.observeForPromoForm();
      }, 100);
    }

    // Also setup on cart updates
    document.addEventListener('cart:updated', () => {
      this.setupPromoCodeHandlers();
      this.updateAppliedCodesDisplay();
    });
    
    // Periodic check as a fallback - try to set up handlers every 2 seconds for the first 10 seconds
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      const forms = document.querySelectorAll('.cart__promo-form:not([data-initialized])');
      if (forms.length > 0) {
        this.setupPromoCodeHandlers();
        clearInterval(interval);
      } else if (attempts >= 5) {
        clearInterval(interval);
      }
    }, 2000);
  }

  observeForPromoForm() {
    // Use MutationObserver to watch for promo form being added to DOM
    if (this.observer) return; // Already observing
    
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Check if promo form was added
          const promoForms = document.querySelectorAll('.cart__promo-form:not([data-initialized])');
          if (promoForms.length > 0) {
            this.setupPromoCodeHandlers();
            this.updateAppliedCodesDisplay();
          }
        }
      }
    });
    
    // Start observing the entire document for changes
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  async refreshCartDisplay() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update applied codes display
      this.updateAppliedCodesDisplayFromCart(cart);
      
      // Update cart totals
      this.updateCartTotals(cart);
      
      // Always update strikethrough pricing when cart refreshes
      if (typeof updateStrikethroughPricing === 'function') {
        updateStrikethroughPricing();
      }
      
      // Re-render per-line-item prices from server-rendered HTML so that
      // line-level discounts (e.g. BOGO) show the struck-through / discounted
      // price on the affected line. The server-side Liquid (cart-item.liquid)
      // renders original_line_price struck through and final_line_price as the
      // sale price whenever they differ, and matches items by their plain key.
      //
      // NOTE: this previously called window.theme.cart.getCart(), which does
      // not exist (the cart store exposes no such method), so it was a silent
      // no-op and the line items never updated after a discount was applied.
      document.querySelectorAll('cart-items').forEach(cartItemsEl => {
        if (typeof cartItemsEl._refreshAllCartItemsHTML === 'function') {
          cartItemsEl._refreshAllCartItemsHTML();
        }
      });

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  updateCartTotals(cart) {
    // Update all cart total elements
    const cartTotalElements = document.querySelectorAll('cart-total');
    const totalPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cart.currency
    }).format(cart.total_price / 100);
    
    cartTotalElements.forEach(element => {
      element.textContent = totalPrice;
    });
    
    // Update strikethrough pricing
    const totalPricesContainer = document.querySelector('.cart__total-prices');
    if (totalPricesContainer) {
      let originalSubtotal = 0;
      cart.items.forEach(item => {
        if (item.compare_at_price && item.compare_at_price > item.price) {
          originalSubtotal += item.compare_at_price * item.quantity;
        } else {
          originalSubtotal += item.original_price * item.quantity;
        }
      });
      
      const totalSavings = originalSubtotal - cart.total_price;
      
      // Show strikethrough if there are any savings (from compare-at prices or discounts)
      if (totalSavings > 0) {
        const formattedOriginal = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: cart.currency
        }).format(originalSubtotal / 100);
        
        totalPricesContainer.innerHTML = `
          <span class="cart__original-total" style="text-decoration: line-through; color: #999; margin-right: 8px;">${formattedOriginal}</span>
          <cart-total class="money cart__final-total" style="color: #28a745; font-weight: 600;">${totalPrice}</cart-total>
        `;
      } else {
        totalPricesContainer.innerHTML = `<cart-total class="money cart__final-total">${totalPrice}</cart-total>`;
      }
    }
  }


  async updateAppliedCodesDisplayFromCart(cart) {
    const appliedCodesDiv = document.querySelector('.cart__applied-codes');
    const appliedCodesList = document.querySelector('.cart__applied-codes-list');
    
    if (!appliedCodesDiv || !appliedCodesList) return;
    
    // Calculate item-level discounts
    let itemDiscountTotal = 0;
    if (cart && cart.items) {
      const productHandles = [...new Set(cart.items.map(item => item.handle))];
      
      // Fetch product data to get compare_at_prices
      const products = await Promise.all(productHandles.map(handle => 
        fetch(`/products/${handle}.js`)
          .then(res => res.json())
          .catch(() => null)
      ));
      
      const variantCompareAtPrices = {};
      products.forEach(product => {
        if (product && product.variants) {
          product.variants.forEach(variant => {
            if (variant.compare_at_price) {
              variantCompareAtPrices[variant.id] = variant.compare_at_price;
            }
          });
        }
      });
      
      // Calculate item-level discounts
      cart.items.forEach(item => {
        const compareAtPrice = variantCompareAtPrices[item.variant_id];
        if (compareAtPrice && compareAtPrice > item.price) {
          const itemDiscount = (compareAtPrice - item.price) * item.quantity;
          itemDiscountTotal += itemDiscount;
        }
        // Include line-level discount allocations (e.g., BOGO automatic discounts)
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
          item.line_level_discount_allocations.forEach(alloc => {
            itemDiscountTotal += alloc.amount;
          });
        }
      });
    }

    const hasDiscounts = (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) || itemDiscountTotal > 0;
    
    if (hasDiscounts) {
      appliedCodesDiv.style.display = 'block';
      
      // Build new content in a document fragment first
      const fragment = document.createDocumentFragment();
      
      // Add item-level discount if exists
      if (itemDiscountTotal > 0) {
        const formattedItemDiscount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: cart.currency
        }).format(itemDiscountTotal / 100);
        
        const itemDiscountDiv = document.createElement('div');
        itemDiscountDiv.className = 'cart__applied-code-item';
        itemDiscountDiv.dataset.code = 'item-discounts';
        itemDiscountDiv.innerHTML = `
          <span class="cart__applied-code-icon">✓</span>
          <span class="cart__applied-code-text">Sale Savings</span>
          <span class="cart__applied-code-amount">-${formattedItemDiscount}</span>
        `;
        fragment.appendChild(itemDiscountDiv);
      }
      
      // Add regular discount codes
      if (cart.cart_level_discount_applications) {
        cart.cart_level_discount_applications.forEach(discount => {
          // Skip if discount title is undefined or null
          if (!discount.title) return;
          
          const formattedAmount = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: cart.currency
          }).format(discount.total_allocated_amount / 100);
          
          const codeItem = document.createElement('div');
          codeItem.className = 'cart__applied-code-item';
          codeItem.dataset.code = discount.title;
          codeItem.innerHTML = `
            <span class="cart__applied-code-icon">✓</span>
            <span class="cart__applied-code-text">${discount.title}</span>
            <span class="cart__applied-code-amount">-${formattedAmount}</span>
          `;
          fragment.appendChild(codeItem);
        });
      }

      // Update DOM with all discounts
      appliedCodesList.innerHTML = '';
      appliedCodesList.appendChild(fragment);
    } else {
      appliedCodesDiv.style.display = 'none';
    }
  }

  updateAppliedCodesDisplay() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        this.updateAppliedCodesDisplayFromCart(cart);
      });
  }

  setupPromoCodeHandlers() {
    const forms = document.querySelectorAll('.cart__promo-form');

    forms.forEach(form => {
      if (form.dataset.initialized) {
        return;
      }
      form.dataset.initialized = true;

      // Handle Enter key in input field
      const input = form.querySelector('.cart__promo-input');
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          const button = form.querySelector('.cart__promo-button');
          button.click(); // Trigger the apply button
        }
      });

      // Create a single handler function
      const handlePromoSubmit = async (e) => {
        e.preventDefault();

        const input = form.querySelector('.cart__promo-input');
        const button = form.querySelector('.cart__promo-button');
        const messageDiv = form.closest('.cart__promo-code').querySelector('.cart__promo-message');
        const successSpan = messageDiv.querySelector('.cart__promo-success');
        const errorSpan = messageDiv.querySelector('.cart__promo-error');

        const code = input.value.trim();

        if (!code) {
          this.showMessage(messageDiv, errorSpan, 'Please enter a promo code');
          return;
        }

        button.disabled = true;
        button.textContent = 'Applying...';

        try {
          // First check if there's an existing discount that needs to be cleared
          const currentCart = await fetch('/cart.js').then(res => res.json());

          if (currentCart.cart_level_discount_applications && currentCart.cart_level_discount_applications.length > 0) {
            // Clear existing discount using form submission to checkout
            const clearFrame = document.createElement('iframe');
            clearFrame.style.display = 'none';
            clearFrame.name = 'clear-discount-frame';
            document.body.appendChild(clearFrame);

            const clearForm = document.createElement('form');
            clearForm.method = 'POST';
            clearForm.action = '/cart';
            clearForm.target = 'clear-discount-frame';
            clearForm.style.display = 'none';

            // Add discount field with empty value
            const discountInput = document.createElement('input');
            discountInput.type = 'hidden';
            discountInput.name = 'discount';
            discountInput.value = '';
            clearForm.appendChild(discountInput);

            document.body.appendChild(clearForm);
            clearForm.submit();

            // Wait for clear attempt
            await new Promise(resolve => setTimeout(resolve, 800));

            // Cleanup
            if (clearForm.parentNode) document.body.removeChild(clearForm);
            if (clearFrame.parentNode) document.body.removeChild(clearFrame);

            // Also try the checkout method as backup
            const checkoutFrame = document.createElement('iframe');
            checkoutFrame.style.display = 'none';
            checkoutFrame.src = '/checkout?discount=';
            document.body.appendChild(checkoutFrame);

            await new Promise(resolve => setTimeout(resolve, 800));
            if (checkoutFrame.parentNode) document.body.removeChild(checkoutFrame);
          }

          // Now apply the new discount code
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
          document.body.appendChild(iframe);

          // Wait for discount to be applied
          await new Promise(resolve => setTimeout(resolve, 1200));
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }

          // Verify the discount was applied
          const verifyResponse = await fetch('/cart.js');
          const verifyCart = await verifyResponse.json();

          const cartLevelApplications = verifyCart.cart_level_discount_applications || [];

          // Collect line-level discount titles too. BOGO (and other product-level)
          // discounts apply to specific line items, so they appear under
          // items[].line_level_discount_allocations and NOT in
          // cart_level_discount_applications. Checking only cart-level would
          // falsely report a valid BOGO code as "invalid or expired".
          const lineLevelTitles = [];
          (verifyCart.items || []).forEach(item => {
            (item.line_level_discount_allocations || []).forEach(alloc => {
              const title = alloc.discount_application && alloc.discount_application.title;
              if (title) lineLevelTitles.push(title);
            });
          });

          const codeMatches = (title) => title && title.toLowerCase() === code.toLowerCase();
          const isApplied = cartLevelApplications.some(d => codeMatches(d.title)) ||
            lineLevelTitles.some(codeMatches);

          if (isApplied) {
            this.showMessage(messageDiv, successSpan, 'Discount applied successfully!');
            input.value = '';
            await this.refreshCartDisplay();
          } else if (cartLevelApplications.length > 0) {
            // A cart-level discount is present but it isn't the code we just entered.
            // Check if the old discount is still there (replacement failed)
            const oldDiscountStillThere = currentCart.cart_level_discount_applications &&
              currentCart.cart_level_discount_applications.some(d =>
                cartLevelApplications.some(v => v.title === d.title)
              );

            if (oldDiscountStillThere) {
              // Old discount couldn't be replaced - try page reload approach
              this.showMessage(messageDiv, errorSpan, 'Reloading to apply new code...');
              // Redirect with the new discount code
              window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
              return;
            } else {
              // A different discount was applied (possibly automatic)
              this.showMessage(messageDiv, successSpan, 'Discount applied!');
              input.value = '';
              await this.refreshCartDisplay();
            }
          } else {
            this.showMessage(messageDiv, errorSpan, 'Invalid or expired promo code');
          }
        } catch (error) {
          this.showMessage(messageDiv, errorSpan, 'An error occurred. Please try again.');
          console.error('Promo code error:', error);
        } finally {
          button.disabled = false;
          button.textContent = button.getAttribute('aria-label') || 'Apply';
        }
      };
      
      // Add handler to both form submit and button click
      form.addEventListener('submit', handlePromoSubmit);
      
      const button = form.querySelector('.cart__promo-button');
      if (button) {
        button.addEventListener('click', handlePromoSubmit);
      }
    });
  }

  showMessage(messageDiv, messageSpan, text) {
    const successSpan = messageDiv.querySelector('.cart__promo-success');
    const errorSpan = messageDiv.querySelector('.cart__promo-error');
    
    successSpan.textContent = '';
    errorSpan.textContent = '';
    successSpan.style.display = 'none';
    errorSpan.style.display = 'none';
    
    messageSpan.textContent = text;
    messageSpan.style.display = 'block';
    messageDiv.style.display = 'block';
    
    setTimeout(() => {
      messageDiv.style.display = 'none';
      messageSpan.style.display = 'none';
      messageSpan.textContent = '';
    }, 5000);
  }
}

// Initialize CartPromoCode when ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartPromoCode = new CartPromoCode();
  });
} else {
  // DOM already loaded
  window.cartPromoCode = new CartPromoCode();
}
